/**
 * highlightText — tokenises a plain-text string and wraps recognised patterns
 * (dates, status keywords, priority keywords) in className-bearing <span>
 * elements for visual emphasis in chat responses.
 *
 * Used by ChatMessage.tsx inside ReactMarkdown component overrides so that
 * the highlighting only applies to prose nodes, never to code blocks or
 * pill-bearing links.
 *
 * Classification rules are documented alongside each pattern below.
 * All date comparisons use Europe/London local time — this is a UK-based
 * app and the Worker emits dates in London time, so a naive Date.parse
 * on "YYYY-MM-DD" and "DD MMM YYYY" strings is correct.
 */

import type { ReactNode } from "react";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Short month name → 0-indexed month number. */
const MONTH_MAP: Record<string, number> = {
	Jan: 0,
	Feb: 1,
	Mar: 2,
	Apr: 3,
	May: 4,
	Jun: 5,
	Jul: 6,
	Aug: 7,
	Sep: 8,
	Oct: 9,
	Nov: 10,
	Dec: 11,
};

/**
 * Multi-word status keywords that must be matched before single-word ones
 * (so "With Client" wins over a hypothetical lone "With").
 */
const STATUS_MULTI: Array<{ phrase: string; className: string }> = [
	{ phrase: "In Progress", className: "hl-status-active" },
	{ phrase: "With Client", className: "hl-status-pending" },
	{ phrase: "Not Required", className: "hl-status-pending" },
];

/** Single-word status keywords, case-sensitive TitleCase. */
const STATUS_SINGLE: Record<string, string> = {
	Done: "hl-status-done",
	Completed: "hl-status-done",
	Open: "hl-status-active",
	Blocked: "hl-status-blocked",
	Delayed: "hl-status-blocked",
};

/** Priority value → CSS class. */
const PRIORITY_CLASS: Record<string, string> = {
	High: "hl-priority-high",
	Medium: "hl-priority-medium",
	Normal: "hl-priority-medium",
	Low: "hl-priority-low",
};

// One day in milliseconds.
const DAY_MS = 24 * 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

/**
 * Midnight London time for "today". In practice we use the local Date
 * constructor — the UK locale plus the small offset window (max 1 hour during
 * BST) makes this indistinguishable from Europe/London for same-day
 * comparisons at day granularity.
 */
function startOfToday(): Date {
	const now = new Date();
	return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/** Parse "YYYY-MM-DD" → Date at local midnight, or null on failure. */
function parseIsoDate(raw: string): Date | null {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
	if (!match) return null;
	const year = Number(match[1]);
	const month = Number(match[2]) - 1;
	const day = Number(match[3]);
	if (month < 0 || month > 11 || day < 1 || day > 31) return null;
	const d = new Date(year, month, day);
	if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) {
		return null;
	}
	return d;
}

/** Parse "DD MMM YYYY" → Date at local midnight, or null on failure. */
function parseDdMmmYyyy(raw: string): Date | null {
	const match = /^(\d{1,2})\s+([A-Z][a-z]{2})\s+(\d{4})$/.exec(raw);
	if (!match) return null;
	const day = Number(match[1]);
	const month = MONTH_MAP[match[2]];
	const year = Number(match[3]);
	if (month === undefined || day < 1 || day > 31) return null;
	const d = new Date(year, month, day);
	if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) {
		return null;
	}
	return d;
}

/**
 * Classify a parsed date relative to today.
 * Returns null when the date should not be highlighted.
 */
function classifyDate(date: Date): "overdue" | "soon" | null {
	const today = startOfToday();
	const diffDays = Math.round((date.getTime() - today.getTime()) / DAY_MS);

	// Today to today+7 inclusive → soon
	if (diffDays >= 0 && diffDays <= 7) return "soon";

	// Past within 90 days → overdue
	if (diffDays < 0 && diffDays >= -90) return "overdue";

	return null;
}

// ---------------------------------------------------------------------------
// Match collection
// ---------------------------------------------------------------------------

interface Match {
	start: number;
	end: number;
	text: string;
	className: string;
}

/**
 * Scan `text` for date patterns and emit highlight matches.
 *
 * NOTE: A recognised date is always wrapped in a span. Dates outside the
 * highlight windows receive an empty className (invisible) so downstream
 * test helpers can distinguish "date found but not highlighted" from "not
 * a date at all".
 */
function collectDateMatches(text: string, out: Match[]): void {
	// ISO dates — 2026-04-05
	const isoRegex = /\b(\d{4}-\d{2}-\d{2})\b/g;
	for (const m of text.matchAll(isoRegex)) {
		const parsed = parseIsoDate(m[1]);
		if (!parsed) continue;
		const verdict = classifyDate(parsed);
		const start = m.index ?? 0;
		out.push({
			start,
			end: start + m[0].length,
			text: m[0],
			className:
				verdict === "overdue" ? "hl-date-overdue" : verdict === "soon" ? "hl-date-soon" : "",
		});
	}

	// DD MMM YYYY — "5 Apr 2026" or "12 Apr 2026"
	const humanRegex = /\b(\d{1,2}\s+[A-Z][a-z]{2}\s+\d{4})\b/g;
	for (const m of text.matchAll(humanRegex)) {
		const parsed = parseDdMmmYyyy(m[1]);
		if (!parsed) continue;
		const verdict = classifyDate(parsed);
		const start = m.index ?? 0;
		out.push({
			start,
			end: start + m[0].length,
			text: m[0],
			className:
				verdict === "overdue" ? "hl-date-overdue" : verdict === "soon" ? "hl-date-soon" : "",
		});
	}
}

/** Scan `text` for status keywords (case-sensitive TitleCase). */
function collectStatusMatches(text: string, out: Match[]): void {
	// Multi-word phrases first
	for (const { phrase, className } of STATUS_MULTI) {
		const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
		// Word boundaries on both sides; note the phrase can contain a space.
		const regex = new RegExp(`\\b${escaped}\\b`, "g");
		for (const m of text.matchAll(regex)) {
			const start = m.index ?? 0;
			out.push({
				start,
				end: start + m[0].length,
				text: m[0],
				className,
			});
		}
	}

	// Single-word TitleCase keywords — must be word-bounded and exact case.
	const singleKeys = Object.keys(STATUS_SINGLE).join("|");
	const regex = new RegExp(`\\b(${singleKeys})\\b`, "g");
	for (const m of text.matchAll(regex)) {
		const start = m.index ?? 0;
		const className = STATUS_SINGLE[m[1]];
		if (!className) continue;
		out.push({
			start,
			end: start + m[0].length,
			text: m[0],
			className,
		});
	}
}

/**
 * Scan `text` for priority keywords in structured contexts only:
 *   - Preceded by a "priority:" label (label-first pattern)
 *   - Or at the start of a markdown list item ("- High", "* High", "1. High")
 *   - Or at the start of a table cell ("|High|", "| High |")
 * Free prose occurrences (e.g. "Set to High priority") must NOT match.
 */
function collectPriorityMatches(text: string, out: Match[]): void {
	const valuePattern = "(High|Medium|Normal|Low)";

	// Pattern 1: "priority:" label (case-insensitive label, exact-case value).
	//
	// We use a capture group for the label so we can compute the value's
	// offset within the match. The value itself is captured with known casing.
	const labelRegex = new RegExp(`(priority\\s*:\\s*)${valuePattern}\\b`, "gi");
	for (const m of text.matchAll(labelRegex)) {
		const value = m[2];
		const className = PRIORITY_CLASS[value];
		if (!className) continue;
		const matchStart = m.index ?? 0;
		const valueStart = matchStart + m[1].length;
		out.push({
			start: valueStart,
			end: valueStart + value.length,
			text: value,
			className,
		});
	}

	// Pattern 2: start of markdown list item — "- High", "* High", "1. High".
	// Must be line-anchored. We allow leading whitespace.
	const listRegex = new RegExp(`(^|\\n)(\\s*(?:[-*]|\\d+\\.)\\s+)${valuePattern}\\b`, "g");
	for (const m of text.matchAll(listRegex)) {
		const value = m[3];
		const className = PRIORITY_CLASS[value];
		if (!className) continue;
		const matchStart = m.index ?? 0;
		const valueStart = matchStart + m[1].length + m[2].length;
		out.push({
			start: valueStart,
			end: valueStart + value.length,
			text: value,
			className,
		});
	}

	// Pattern 3: table cell — "|High|" or "| High |".
	const tableRegex = new RegExp(`\\|\\s*${valuePattern}\\s*\\|`, "g");
	for (const m of text.matchAll(tableRegex)) {
		const value = m[1];
		const className = PRIORITY_CLASS[value];
		if (!className) continue;
		const matchStart = m.index ?? 0;
		// Locate the value within the match text; the regex may include
		// variable whitespace between "|" and the value.
		const relative = m[0].indexOf(value);
		const valueStart = matchStart + relative;
		out.push({
			start: valueStart,
			end: valueStart + value.length,
			text: value,
			className,
		});
	}

	// Pattern 4 (neutral fallback): any standalone occurrence of a priority
	// keyword (case-insensitive) gets wrapped in a span with NO class. This
	// allows downstream test helpers and the overlap resolver to distinguish
	// "word found but not in structured context" from "structured priority"
	// without visually affecting the rendered text.
	const neutralRegex = new RegExp(`\\b${valuePattern}\\b`, "gi");
	for (const m of text.matchAll(neutralRegex)) {
		const value = m[1];
		const start = m.index ?? 0;
		out.push({
			start,
			end: start + value.length,
			text: value,
			className: "",
		});
	}
}

/**
 * Deduplicate / resolve overlapping matches.
 *
 * Ordering rules (applied in turn):
 *   1. Earlier start first
 *   2. Classed matches beat neutral (empty className) matches at the same start
 *   3. Longer match beats shorter
 *
 * Any match whose span overlaps a previously accepted match is discarded,
 * UNLESS the incoming match is classed and the previously accepted one is
 * neutral and fully covers the same range — in which case we replace.
 */
function resolveOverlaps(matches: Match[]): Match[] {
	const sorted = [...matches].sort((a, b) => {
		if (a.start !== b.start) return a.start - b.start;
		// Classed beats neutral at the same start
		const aNeutral = a.className === "";
		const bNeutral = b.className === "";
		if (aNeutral !== bNeutral) return aNeutral ? 1 : -1;
		// Longer wins
		return b.end - b.start - (a.end - a.start);
	});
	const accepted: Match[] = [];
	let cursor = 0;
	for (const m of sorted) {
		if (m.start < cursor) continue;
		accepted.push(m);
		cursor = m.end;
	}
	return accepted;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Tokenise a plain-text string and return an array of ReactNodes where
 * matched patterns are wrapped in <span className="hl-..."> elements and
 * unmatched segments are plain strings.
 */
export function highlightText(text: string): ReactNode[] {
	if (!text) return [];

	const raw: Match[] = [];
	collectDateMatches(text, raw);
	collectStatusMatches(text, raw);
	collectPriorityMatches(text, raw);

	const matches = resolveOverlaps(raw);
	if (matches.length === 0) return [text];

	const nodes: ReactNode[] = [];
	let cursor = 0;
	let keyIndex = 0;
	for (const m of matches) {
		if (m.start > cursor) {
			nodes.push(text.slice(cursor, m.start));
		}
		nodes.push(
			<span key={`hl-${keyIndex++}-${m.start}`} className={m.className}>
				{m.text}
			</span>,
		);
		cursor = m.end;
	}
	if (cursor < text.length) {
		nodes.push(text.slice(cursor));
	}
	return nodes;
}
